import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'

// Check if current user exists in users table
export const useUserCheck = (userId: string) => {
  return useQuery({
    queryKey: ['userCheck', userId],
    queryFn: async () => {
      if (!userId) {
        return null
      }

      const { data, error } = await supabase
        .from('users')
        .select('*, company:companies(*)')
        .eq('id', userId)
        .single()
      
      if (error && error.code !== 'PGRST116') {
        console.error('User not found in users table:', error)
        return null
      }

      if (data) {
        return data
      }

      // Attempt to create (or recover) the missing profile
      const { data: { user }, error: authError } = await supabase.auth.getUser()
      if (authError || !user || user.id !== userId) {
        console.error('Unable to load authenticated user metadata', authError)
        return null
      }

      const metadata = user.user_metadata ?? {}

      const insertPayload = {
        id: user.id,
        email: user.email ?? '',
        role: (metadata.role as 'admin' | 'manager' | 'employee') ?? 'manager',
        company_id: metadata.company_id ?? null,
        full_name: (metadata.full_name as string | null) ?? user.email ?? '',
        is_active: true,
        onboarding_completed: Boolean(metadata.onboarding_completed ?? false),
        onboarding_completed_at: metadata.onboarding_completed ? new Date().toISOString() : null
      }

      const { data: upsertedUser, error: upsertError } = await supabase
        .from('users')
        .upsert(insertPayload, { onConflict: 'id' })
        .select('*, company:companies(*)')
        .single()

      if (upsertError) {
        console.error('Failed to upsert missing user record:', upsertError)

        // Last attempt: fetch the record again in case of race condition
        const { data: retryData } = await supabase
          .from('users')
          .select('*, company:companies(*)')
          .eq('id', userId)
          .maybeSingle()

        return retryData ?? null
      }

      return upsertedUser
    },
    enabled: !!userId,
  })
}
